/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-29 07:46:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Button } from '@shopify/polaris';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';

import ExportModal from './export-modal';
// import { RenderSelect2 } from 'src/components/views/common/render-select-2';
import { RenderDateRangePicker } from 'src/components/views/analytics-v2/render-date-range-picker';
import { RenderTooltip } from 'src/components/views/common/render-tooltip';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import {
  EXPORT_MODAL_TITLE_SEARCH_ANALYTICS,
  EXPORT_MODAL_TITLE_COLLECTION_ANALYTICS,
  EXPORT_MODAL_TITLE_RECOMMENDATION_ANALYTICS,
  FEATURES
} from 'src/constants/analytics';
import { ANALYTICS_ADDON, ANALYTICS_ADDON_NAME } from 'src/constants/subscription';
import { getAddonAnalyticInfos } from 'src/helpers/analytics-v2-helpers.js';

import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';

type Props = {
  fromDateStateName: string,
  toDateStateName: string,
  compareStateName: string,
  metric?: string,
  showCompare: boolean,
  feature?: string
};
type ComposedProps = Props & WithI18nProps;
type State = {
  activeExport: boolean
};
class Filter extends Component<ComposedProps, State> {
  state = {
    activeExport: false
  };

  render() {
    const {
      collections,
      collection,
      fromDate,
      toDate,
      compare,
      appLanguage,
      metric,
      showCompare,
      feature,
      statusStore,
      addonStatus,
      openReviewAddonModal,
      invoice,
      notAllowAddon,
      featureAnalytics,
      i18n
    } = this.props;

    const { activeExport } = this.state;

    const { addonInfo = {} } = getAddonAnalyticInfos(invoice, addonStatus, featureAnalytics);
    const addonName = _.get(addonInfo, 'addonName', null);
    const addonValue = ANALYTICS_ADDON.find(e => e.name === addonName);

    const allowTrialAddon = ['trial', 'expiredSoon'].includes(statusStore);

    const limitDays = parseInt(_.get(addonValue, 'value', 0)) || 30;
    const allCollection = {
      value: 'all',
      label: i18n.translate('AdminHTML.Analytics.Common.allCollections')
    };

    let optionCollections = [];
    if (feature === FEATURES.COLLECTION) {
      optionCollections = [...collections];
      optionCollections.unshift(allCollection);
    }

    const modalTitle = this.getModalTitle();
    return (
      <div className="BC-Analytics__Filter">
        {/* {feature === FEATURES.COLLECTION && (
          <React.Fragment>
            <div className="filter-collection">
              <RenderSelect2
                name="collection"
                options={optionCollections}
                value={collection}
                onChange={value => this.onChangeSelect('collection', value)}
                updateStyle={styleSelect}
                isDisabled={analyticLoading}
              />
            </div>
            <span className="separator">-</span>
          </React.Fragment>
        )} */}
        <RenderDateRangePicker
          feature={feature}
          fromDate={fromDate}
          toDate={toDate}
          compare={compare}
          allowTrial={allowTrialAddon}
          onChooseDate={this.onChooseDate}
          appLanguage={appLanguage}
          showCompare={showCompare}
          limitDays={limitDays}
          disableLockUnLock={notAllowAddon}
          openReviewAddonModal={openReviewAddonModal}
        />
        <RenderTooltip
          content={i18n.translate('AdminHTML.Analytics.Common.tooltip_DataSyncProcessInterval')}
          icon={true}
        />
        <div style={{ marginLeft: 'auto' }}>
          <Button outline onClick={this.handleChangeModal}>
            {i18n.translate('AdminHTML.Analytics.Common.export')}
          </Button>
        </div>
        <ExportModal
          active={activeExport}
          optionCollections={optionCollections}
          collection={collection}
          fromDate={fromDate}
          toDate={toDate}
          handleChangeModal={this.handleChangeModal}
          appLanguage={appLanguage}
          metric={metric}
          feature={feature}
          title={modalTitle}
          allowTrial={allowTrialAddon}
          limitDays={limitDays}
          disableLockUnLock={notAllowAddon}
          openReviewAddonModal={openReviewAddonModal}
        />
      </div>
    );
  }

  componentDidMount() {
    this.props.getThemes();
  }

  getModalTitle = () => {
    const { i18n, metric, feature } = this.props;
    let modalTitle = '';
    switch (feature) {
      case FEATURES.APP_IMPACT:
        modalTitle = i18n.translate('AdminHTML.Analytics.ExportModal.title_AppImpact');
        break;
      case FEATURES.COLLECTION:
        modalTitle = i18n.translate(EXPORT_MODAL_TITLE_COLLECTION_ANALYTICS[metric]);
        break;
      case FEATURES.RECOMMENDATION:
        modalTitle = i18n.translate(EXPORT_MODAL_TITLE_RECOMMENDATION_ANALYTICS[metric]);
        break;
      case FEATURES.SEARCH:
        modalTitle = i18n.translate(EXPORT_MODAL_TITLE_SEARCH_ANALYTICS[metric]);
        break;
      default:
        break;
    }
    return modalTitle;
  };

  handleChangeModal = () => {
    this.setState(({ activeExport }) => ({ activeExport: !activeExport }));
  };

  onChooseDate = (selectedDate, compare) => {
    const {
      changeValue,
      performActionSurvey,
      trackEvent,
      fromDateStateName,
      toDateStateName,
      compareStateName,
      metric
    } = this.props;
    let { start, end } = selectedDate;

    changeValue(fromDateStateName, start);
    changeValue(toDateStateName, end);
    changeValue(compareStateName, compare);
    changeValue('pathname', metric);

    // Show survey when user change date
    performActionSurvey();
    trackEvent('csatFeedback', 'changeDatePicker', 'csatFeedback_changeDatePicker');
    Emitter.emit(EVENT_KEY.RELOAD_CSAT_ADMIN_APP_DISPLAY);
  };

  onChangeSelect(name, value) {
    this.props.changeValue(name, value);
    this.props.trackEvent('csatFeedback', 'changeDatePicker', 'csatFeedback_changeDatePicker');
    Emitter.emit(EVENT_KEY.RELOAD_CSAT_ADMIN_APP_DISPLAY);
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  changeValue: (name, value) => dispatch(AnalyticsActions.changeValue(name, value)),
  getThemes: () => dispatch(themeSetupV3Action.getThemes(ownProps.i18n)),
  trackEvent: (eventSource, eventName, label) =>
    dispatch(checklistActions.trackEvent(eventSource, eventName, label))
});

const mapStateToProps = (state, props) => {
  // Check if one of analytic metrics is loading
  const { fromDateStateName, toDateStateName, compareStateName } = props;
  const analyticLoading = _.find(state.analyticsV2, (value, key) => {
    return value === true && key.includes('Loading');
  });

  return {
    collections: _.get(state.filterData.filterData, 'collections', []),
    statusStore: _.get(state.oauth, 'subscription.status', ''),
    notAllowAddon: _.get(state.oauth, 'subscription.notAllowAddon', false),
    collection: _.get(state.analyticsV2, 'collection', {
      value: 'all',
      label: props.i18n.translate('AdminHTML.Analytics.Common.allCollections')
    }),
    fromDate: _.get(state.analyticsV2, fromDateStateName),
    toDate: _.get(state.analyticsV2, toDateStateName),
    compare: _.get(state.analyticsV2, compareStateName, false),
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE),
    analyticLoading,
    addonStatus: _.get(state.subscriptionV3, `addonStatus.${ANALYTICS_ADDON_NAME}`, {}),
    invoice: _.get(state.subscriptionV3, 'invoice', {}),
    featureAnalytics: _.get(state.oauth.tenantApp, 'settings.analyticV2.settings', {})
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Filter));

// const styleSelect = {
//   dropdownIndicator: { display: 'flex' },
//   menuPortal: { minWidth: '500px' }
// };
